import React, { FC } from "react"
import type { BlackoutDate } from "../../Api/BlackoutDateApi"

interface CalendarProps {
  currentMonth: Date
  blackoutDates: BlackoutDate[]
  onDateClick: (date: Date, blackout?: BlackoutDate) => void
  onPreviousMonth: () => void
  onNextMonth: () => void
}

const Calendar: FC<CalendarProps> = ({
  currentMonth,
  blackoutDates,
  onDateClick,
  onPreviousMonth,
  onNextMonth
}) => {
  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  
  // 月の最初の日と最後の日を取得
  const firstDay = new Date(year, month, 1)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const formatDateForKey = (date: Date): string => {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  
  const getBlackoutForDate = (date: Date): BlackoutDate | undefined => {
    const dateStr = formatDateForKey(date)
    // is_activeがtrueのもののみを予約不可日として扱う
    // バックエンドからはYYYY-MM-DD形式で返ってくる
    return blackoutDates.find(b => {
      if (!b.is_active) return false
      // dateフィールドがYYYY-MM-DD形式かYYYY-MM-DDTHH:mm:ss形式のどちらでも対応
      const blackoutDate = b.date ? (b.date.includes('T') ? b.date.split('T')[0] : b.date) : ''
      return blackoutDate === dateStr
    })
  }
  
  const renderDayCell = (date: Date) => {
    const isOtherMonth = date.getMonth() !== month
    const isToday = date.getTime() === today.getTime()
    const isPast = date < today
    const dayOfWeek = date.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6 // 日曜=0、土曜=6
    
    // is_activeがtrueのもののみを予約不可日として扱う
    const blackout = getBlackoutForDate(date)
    
    // 背景色の決定
    let bgColor = 'bg-white'
    if (isOtherMonth) {
      bgColor = 'bg-gray-50'
    } else if (isPast) {
      bgColor = 'bg-gray-50'
    } else if (isWeekend) {
      bgColor = 'bg-gray-100' // 土日はグレー背景
    } else if (isToday && !blackout) {
      bgColor = 'bg-yellow-100'
    } else if (blackout) {
      // 予約不可日はピンク色
      bgColor = 'bg-pink-100'
    }
    
    // ボーダー色の決定
    let borderColor = 'border-gray-200'
    if (blackout) {
      borderColor = 'border-pink-500'
    }
    
    // カーソルとホバーの決定
    let cursorClass = ''
    let hoverClass = ''
    if (isOtherMonth || isPast || isWeekend) {
      cursorClass = isOtherMonth ? 'cursor-default' : 'cursor-not-allowed'
    } else {
      cursorClass = 'cursor-pointer'
      hoverClass = 'hover:bg-blue-50 hover:scale-105 hover:shadow-md hover:z-10'
    }
    
    return (
      <div
        key={date.toISOString()}
        className={`h-20 p-2 border ${borderColor} ${bgColor} ${cursorClass} ${hoverClass} transition-all relative flex flex-col items-center justify-start overflow-hidden ${
          isToday ? 'font-bold' : ''
        } ${isWeekend && !isOtherMonth ? 'opacity-60' : ''}`}
        onClick={() => {
          if (!isOtherMonth && !isPast && !isWeekend) {
            onDateClick(date, blackout)
          }
        }}
      >
        {/* 日付番号 */}
        <div className={`text-base font-medium mb-1 flex-shrink-0 leading-none ${
          isToday
            ? 'bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center'
            : blackout
            ? 'text-pink-700 font-bold'
            : 'text-gray-900'
        }`}>
          {date.getDate()}
        </div>
        
        {/* 予約不可日のインジケーター */}
        {blackout && (
          <>
            <div className="absolute top-2 right-2 w-2 h-2 bg-pink-500 rounded-full"></div>
            <div className="text-xs font-semibold mt-1 text-center flex-shrink-0 leading-none overflow-hidden text-ellipsis whitespace-nowrap max-w-full px-0.5 text-pink-700">
              予約不可
            </div>
            {blackout.reason && (
              <div className="text-xs text-gray-700 text-center overflow-hidden text-ellipsis whitespace-nowrap max-w-full mt-auto flex-1 min-h-0 leading-tight px-0.5" title={blackout.reason}>
                {blackout.reason}
              </div>
            )}
          </>
        )}
      </div>
    )
  }
  
  const weekdays = ['日', '月', '火', '水', '木', '金', '土']
  
  return (
    <div className="w-full overflow-x-auto -webkit-overflow-scrolling-touch">
      <div className="min-w-[320px]">
        {/* 曜日ヘッダー */}
        <div className="grid grid-cols-7 gap-0 mb-2">
          {weekdays.map((day, index) => (
            <div
              key={day}
              className={`p-3 text-center font-semibold text-sm text-gray-900 bg-gray-50 border border-gray-200 ${
                index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : ''
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* カレンダーグリッド */}
        <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
          {Array.from({ length: 42 }, (_, i) => {
            const currentDate = new Date(startDate)
            currentDate.setDate(startDate.getDate() + i)
            return renderDayCell(currentDate)
          })}
        </div>
      </div>
    </div>
  )
}

export default Calendar

